import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

type HeightLimits = {
  initialHeight: number;
  minHeight: number;
  maxHeight: number;
};
interface AccordionContextType {
  height: number;
  minHeight: number;
  maxHeight: number;
  setHeight: (height: number) => void;
  setHeightLimits: (limits: HeightLimits) => void;
}

const AccordionContext = createContext<AccordionContextType | undefined>(
  undefined
);

export const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error(
      "useAccordionContext must be used within an AccordionProvider"
    );
  }
  return context;
};

interface AccordionProviderProps {
  children: ReactNode;
}

export const AccordionProvider = ({ children }: AccordionProviderProps) => {
  const [height, setHeightState] = useState(0);
  const [minHeight, setMinHeight] = useState(48);
  const [maxHeight, setMaxHeight] = useState(0);

  const setHeight = useCallback(
    (newHeight: number) => {
      if (newHeight === height) return;

      const clampedHeight = Math.min(Math.max(newHeight, minHeight), maxHeight);
      setHeightState(clampedHeight);
    },
    [minHeight, maxHeight, height]
  );

  const setHeightLimits = useCallback(
    ({ initialHeight, minHeight, maxHeight }: HeightLimits) => {
      setHeightState(initialHeight);
      setMinHeight(minHeight);
      setMaxHeight(maxHeight);
    },
    []
  );

  return (
    <AccordionContext.Provider
      value={{ height, minHeight, maxHeight, setHeight, setHeightLimits }}
    >
      {children}
    </AccordionContext.Provider>
  );
};
